import React, { useState, useEffect, useContext, useRef, useMemo, useDeferredValue } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import LoadingSpinner from '../components/LoadingSpinner';
import TaskCreateModal from '../components/TaskCreateModal';
import TaskDraftModal from '../components/TaskDraftModal';
import TaskListSection from '../components/TaskListSection';
import TasksFiltersBar from '../components/TasksFiltersBar';
import TasksOverviewBanner from '../components/TasksOverviewBanner';
import WorkGroupAssignAllModal from '../components/WorkGroupAssignAllModal';
import { toDateKey } from '../components/TasksCalendarView';
import { fetchCompanyUsers } from '../utils/companyUsers';
import {
  filterRemovedTasks,
  filterTodayTasks,
  filterUpcomingTasks,
  isCompletedToday,
  isTaskRemoved
} from '../utils/taskFilters';
import { isTaskClaimedOrAssigned } from '../utils/taskClaim';
import { canAccessFeature, isCompanyAdmin, isCompanyEmployee, canSubmitTaskDraft } from '../utils/planAccess';
import './Tasks.css';

const TASK_SECTION_IDS = {
  today: 'tasks-section-today',
  upcoming: 'tasks-section-upcoming',
  completed: 'tasks-section-completed'
};

const emptyFormData = () => ({
  title: '',
  description: '',
  priority: 'medium',
  estimatedDuration: '',
  location: '',
  locationId: '',
  locationName: '',
  customerId: '',
  articleId: '',
  articleQuantity: '',
  workGroupId: '',
  workGroupName: '',
  checklist: [],
  scheduledFor: '',
  recurrenceEnabled: false,
  recurrenceIntervalDays: '',
  recurrenceWeekdays: []
});

const sortTodayTasks = (tasks) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const getTaskDate = (task) => {
    const dateStr = task.scheduled_for || task.created_at;
    if (!dateStr) return new Date(0);
    const date = new Date(dateStr);
    return Number.isNaN(date.getTime()) ? new Date(0) : date;
  };

  return [...tasks].sort((a, b) => {
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    const priorityDiff = (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    const dateA = getTaskDate(a);
    const dateB = getTaskDate(b);
    const isOverdueA = dateA < today;
    const isOverdueB = dateB < today;
    if (isOverdueA && !isOverdueB) return -1;
    if (!isOverdueA && isOverdueB) return 1;
    const dateDiff = dateA - dateB;
    if (dateDiff !== 0) {
      return dateDiff;
    }
    return Number(a.id) - Number(b.id);
  });
};

const Tasks = () => {
  const { t } = useTranslation(['tasks', 'common']);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusSection = searchParams.get('section');
  const openCreateFromQuery = searchParams.get('create') === '1';
  const [tasks, setTasks] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [workGroups, setWorkGroups] = useState([]);
  const [workGroupFilterId, setWorkGroupFilterId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('');
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(taskSearchQuery);
  const [showAssignAllModal, setShowAssignAllModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [draftFormData, setDraftFormData] = useState({ title: '', description: '' });
  const [draftBannerFile, setDraftBannerFile] = useState(null);
  const draftBannerInputRef = useRef(null);
  const [formData, setFormData] = useState(emptyFormData());
  const [bannerFile, setBannerFile] = useState(null);
  const bannerInputRef = useRef(null);
  const [error, setError] = useState('');
  const [presetChecklists, setPresetChecklists] = useState([]);
  const [taskPresets, setTaskPresets] = useState([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [upcomingViewMode, setUpcomingViewMode] = useState('list');
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(() => toDateKey(new Date()));
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const canUseWorkGroups = isCompanyAdmin(user) && canAccessFeature(user, 'taskWorkGroups');

  useEffect(() => {
    localStorage.setItem('tasks_last_visit', new Date().toISOString());
    window.dispatchEvent(new CustomEvent('tasksPageVisited'));

    fetchDrafts();
    if (isCompanyAdmin(user)) {
      fetchLocations();
      fetchCustomers();
      fetchArticles();
      if (canAccessFeature(user, 'checklistPresets')) {
        fetchPresetChecklists();
      }
      if (canAccessFeature(user, 'taskPresets')) {
        fetchTaskPresets();
      }
      if (canAccessFeature(user, 'taskWorkGroups')) {
        fetchWorkGroups();
      } else {
        setWorkGroups([]);
      }
    }
  }, [user?.role, user?.active_company_id, user?.active_company_role]);

  useEffect(() => {
    fetchTasks();
  }, [user?.role, user?.active_company_id, user?.active_company_role, includeRemoved]);

  useEffect(() => {
    const loadAssignableUsers = async () => {
      if ((!showModal && !showAssignAllModal) || !isCompanyAdmin(user)) {
        return;
      }

      const companyId = user.active_company_id || user.company_id;
      if (!companyId) {
        return;
      }

      try {
        const companyUsers = await fetchCompanyUsers(companyId, user);
        setAssignableUsers(companyUsers);
      } catch (err) {
        console.error('Error fetching assignable users:', err);
      }
    };

    loadAssignableUsers();
  }, [showModal, showAssignAllModal, user]);

  const fetchDrafts = async () => {
    if (!canSubmitTaskDraft(user)) {
      setDrafts([]);
      return;
    }

    try {
      const response = await axios.get('/api/tasks/drafts');
      setDrafts(response.data.data);
    } catch (err) {
      console.error('Error fetching drafts:', err);
      setError(formatError(err) || t('tasks:failedFetchDrafts'));
    }
  };

  const fetchTasks = async () => {
    try {
      const params = {};
      if (includeRemoved && isCompanyAdmin(user)) {
        params.include_removed = 1;
      }
      const response = await axios.get('/api/tasks', { params });
      setTasks(response.data.data);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(t('tasks:failedFetchTasks'));
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations');
      setLocations(response.data.data);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/customers');
      setCustomers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setCustomers([]);
    }
  };

  const fetchArticles = async () => {
    try {
      const companyId = user?.active_company_id || user?.company_id;
      const response = await axios.get('/api/articles', {
        params: companyId ? { companyId } : undefined
      });
      setArticles(response.data.data || []);
    } catch (err) {
      setArticles([]);
      if (err.response?.status !== 403) {
        console.error('Error fetching articles:', err);
      }
    }
  };

  const fetchWorkGroups = async () => {
    try {
      const response = await axios.get('/api/work-groups');
      setWorkGroups(response.data.data || []);
    } catch (err) {
      setWorkGroups([]);
      if (err.response?.status !== 403) {
        console.error('Error fetching work groups:', err);
      }
    }
  };

  const fetchPresetChecklists = async () => {
    try {
      const response = await axios.get('/api/preset-checklists');
      setPresetChecklists(response.data.data || []);
    } catch (err) {
      setPresetChecklists([]);
      if (err.response?.status !== 403) {
        console.error('Error fetching preset checklists:', err);
      }
    }
  };

  const fetchTaskPresets = async () => {
    try {
      const response = await axios.get('/api/task-presets');
      const normalizedPresets = (response.data.data || []).map((preset) => {
        if (preset.checklist && Array.isArray(preset.checklist)) {
          preset.checklist = preset.checklist.map((item) => {
            if (typeof item === 'string') {
              return { text: item, checked: false };
            }
            if (item && typeof item === 'object' && item !== null) {
              return {
                text: item.text || '',
                checked: item.checked || false
              };
            }
            return { text: String(item || ''), checked: false };
          });
        } else {
          preset.checklist = [];
        }
        return preset;
      });
      setTaskPresets(normalizedPresets);
    } catch (err) {
      setTaskPresets([]);
      if (err.response?.status !== 403) {
        console.error('Error fetching task presets:', err);
      }
    }
  };

  const handleAddChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setFormData((prev) => ({
        ...prev,
        checklist: [...prev.checklist, newChecklistItem.trim()]
      }));
      setNewChecklistItem('');
    }
  };

  const handleRemoveChecklistItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  };

  const handlePresetChecklistChange = (e) => {
    const presetId = e.target.value;
    if (presetId) {
      const preset = presetChecklists.find((p) => p.id === parseInt(presetId, 10));
      if (preset && preset.items) {
        setFormData((prev) => ({
          ...prev,
          checklist: Array.isArray(preset.items) ? [...preset.items] : []
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        checklist: []
      }));
    }
  };

  const handleTaskPresetChange = (e) => {
    const presetId = e.target.value;
    if (presetId) {
      const preset = taskPresets.find((p) => p.id === parseInt(presetId, 10));
      if (preset) {
        let checklist = [];
        if (preset.checklist) {
          if (Array.isArray(preset.checklist)) {
            checklist = preset.checklist.map((item) => {
              if (typeof item === 'string') {
                return { text: item, checked: false };
              }
              let textValue = '';
              if (item && typeof item === 'object' && item !== null) {
                textValue = item.text || '';
              } else {
                textValue = String(item || '');
              }
              return { text: textValue, checked: item?.checked || false };
            });
          }
        }

        setFormData((prev) => ({
          ...prev,
          title: preset.title || prev.title,
          description: preset.description || prev.description,
          priority: preset.priority || 'medium',
          estimatedDuration: preset.estimated_duration || prev.estimatedDuration,
          checklist,
          recurrenceEnabled: preset.recurrence_enabled || false,
          recurrenceIntervalDays: preset.recurrence_weekdays?.length
            ? ''
            : (preset.recurrence_interval_days || prev.recurrenceIntervalDays),
          recurrenceWeekdays: Array.isArray(preset.recurrence_weekdays)
            ? preset.recurrence_weekdays.map(Number).filter((day) => day >= 0 && day <= 6)
            : []
        }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      if (name === 'locationName') {
        const matchingLocation = locations.find(
          (loc) => loc.name.toLowerCase() === value.toLowerCase()
        );
        if (matchingLocation) {
          updated.locationId = matchingLocation.id;
        } else {
          updated.locationId = '';
        }
      }
      if (name === 'articleId') {
        const selected = articles.find((article) => String(article.id) === String(value));
        if (!selected || selected.unit_mode === 'time') {
          updated.articleQuantity = '';
        }
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const payload = new FormData();
      payload.append('title', formData.title);
      payload.append('description', formData.description || '');
      payload.append('priority', formData.priority || 'medium');
      payload.append('estimatedDuration', formData.estimatedDuration || '');
      payload.append('location', formData.location || '');
      const checklistForSubmit = (formData.checklist || []).map((item) => {
        if (typeof item === 'string') {
          return { text: item, checked: false };
        }
        let textValue = '';
        if (item && typeof item === 'object' && item !== null) {
          textValue = item.text || '';
        } else {
          textValue = String(item || '');
        }
        return { text: textValue, checked: item?.checked || false };
      });
      payload.append('checklist', JSON.stringify(checklistForSubmit));
      if (formData.scheduledFor) {
        payload.append('scheduledFor', formData.scheduledFor);
      }
      if (formData.recurrenceEnabled && (!formData.recurrenceWeekdays || formData.recurrenceWeekdays.length === 0)
          && !formData.recurrenceIntervalDays) {
        setError(t('tasks:recurrenceWeekdaysRequired'));
        return;
      }
      payload.append('recurrenceEnabled', formData.recurrenceEnabled ? 'true' : 'false');
      payload.append(
        'recurrenceWeekdays',
        JSON.stringify(formData.recurrenceEnabled ? (formData.recurrenceWeekdays || []) : [])
      );
      payload.append(
        'recurrenceIntervalDays',
        formData.recurrenceEnabled && formData.recurrenceWeekdays?.length
          ? ''
          : (formData.recurrenceIntervalDays || '')
      );
      if (formData.locationId) {
        payload.append('locationId', formData.locationId);
      } else if (formData.locationName && formData.locationName.trim() !== '') {
        payload.append('location', formData.locationName.trim());
      }
      if (formData.customerId) {
        payload.append('customerId', formData.customerId);
      }
      if (formData.articleId) {
        payload.append('articleId', formData.articleId);
      }
      if (formData.articleQuantity !== '' && formData.articleQuantity != null) {
        payload.append('articleQuantity', formData.articleQuantity);
      }
      if (formData.workGroupName) {
        payload.append('workGroupName', formData.workGroupName);
      } else if (formData.workGroupId) {
        payload.append('workGroupId', formData.workGroupId);
      }
      if (bannerFile) {
        payload.append('banner', bannerFile);
      }
      if (selectedAssignees.length > 0) {
        payload.append('assignUserIds', JSON.stringify(selectedAssignees.map((assignee) => assignee.id)));
      }

      await axios.post('/api/tasks', payload);
      handleCloseModal();
      fetchTasks();
      if (canUseWorkGroups) {
        fetchWorkGroups();
      }
    } catch (err) {
      setError(formatError(err) || t('tasks:failedCreateTask'));
    }
  };

  const handleCloseDraftModal = () => {
    setShowDraftModal(false);
    setEditingDraftId(null);
    setDraftFormData({ title: '', description: '' });
    setDraftBannerFile(null);
    if (draftBannerInputRef.current) {
      draftBannerInputRef.current.value = '';
    }
  };

  const openDraftModal = (draft = null) => {
    if (draft) {
      setEditingDraftId(draft.id);
      setDraftFormData({
        title: draft.title || '',
        description: draft.description || ''
      });
    } else {
      setEditingDraftId(null);
      setDraftFormData({ title: '', description: '' });
    }
    setDraftBannerFile(null);
    if (draftBannerInputRef.current) {
      draftBannerInputRef.current.value = '';
    }
    setShowDraftModal(true);
  };

  const handleDraftSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const payload = new FormData();
      payload.append('title', draftFormData.title);
      payload.append('description', draftFormData.description || '');

      if (draftBannerFile) {
        payload.append('banner', draftBannerFile);
      }

      if (editingDraftId) {
        await axios.put(`/api/tasks/${editingDraftId}`, payload);
      } else {
        payload.append('status', 'draft');
        await axios.post('/api/tasks', payload);
      }

      handleCloseDraftModal();
      fetchDrafts();
    } catch (err) {
      setError(formatError(err) || t('tasks:failedSaveDraft'));
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError('');
    setFormData(emptyFormData());
    setNewChecklistItem('');
    setSelectedAssignees([]);
    setBannerFile(null);
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
  };

  const openCreateModal = (scheduledFor = '') => {
    const initial = emptyFormData();
    if (typeof scheduledFor === 'string' && scheduledFor) {
      initial.scheduledFor = scheduledFor;
    }
    setFormData(initial);
    setBannerFile(null);
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
    setShowModal(true);
  };

  const openCreateFromCalendarSelection = () => {
    openCreateModal(selectedDay || '');
  };

  const createQueryHandledRef = useRef(false);
  useEffect(() => {
    if (!openCreateFromQuery) {
      createQueryHandledRef.current = false;
      return;
    }
    if (loading || createQueryHandledRef.current) {
      return;
    }
    createQueryHandledRef.current = true;
    if (isCompanyAdmin(user)) {
      openCreateModal();
    }
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    const search = next.toString();
    navigate({ search: search ? `?${search}` : '' }, { replace: true });
  }, [loading, openCreateFromQuery, user, searchParams, navigate]);

  const handleClaim = async (id) => {
    const taskId = Number(id);
    try {
      await axios.post(`/api/tasks/${id}/claim`);
      // Update in place so Today/overview order does not reshuffle on refetch.
      const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
        || user?.email
        || '';
      setTasks((prev) => prev.map((task) => {
        if (Number(task.id) !== taskId) {
          return task;
        }
        return {
          ...task,
          status: 'assigned',
          assignment_count: 1,
          assigned_user_names: displayName,
          assigned_users: [{
            id: user.id,
            firstName: user.first_name || '',
            lastName: user.last_name || '',
            profile_image_url: user.profile_image_url || user.profile_image || null
          }]
        };
      }));
    } catch (err) {
      setError(formatError(err) || t('tasks:failedClaimTask'));
    }
  };

  const handleRowClick = (taskId) => {
    navigate(`/tasks/${taskId}`);
  };

  const handleNavigateDrafts = () => {
    if (!drafts.length) {
      return;
    }
    navigate(`/tasks/${drafts[0].id}`);
  };

  const todayKey = toDateKey(new Date());

  const customerNameById = useMemo(() => {
    const map = new Map();
    customers.forEach((customer) => {
      if (customer?.id == null) {
        return;
      }
      map.set(String(customer.id), customer.name || '');
    });
    return map;
  }, [customers]);

  const tasksForLists = useMemo(() => {
    const query = String(deferredSearchQuery || '').trim().toLowerCase();
    const hasFilters = Boolean(
      workGroupFilterId || statusFilter || assignmentFilter === 'unassigned' || query
    );
    if (!hasFilters) {
      return tasks;
    }
    return tasks.filter((task) => {
      if (workGroupFilterId && String(task.work_group_id) !== String(workGroupFilterId)) {
        return false;
      }
      if (statusFilter && task.status !== statusFilter) {
        return false;
      }
      if (assignmentFilter === 'unassigned' && isTaskClaimedOrAssigned(task)) {
        return false;
      }
      if (!query) {
        return true;
      }
      const customerName = task.customer_name
        || customerNameById.get(String(task.customer_id))
        || '';
      const fields = [
        task.title,
        task.description,
        customerName,
        task.location_name,
        task.location,
        task.work_group_name
      ];
      for (let i = 0; i < fields.length; i += 1) {
        const value = fields[i];
        if (value && String(value).toLowerCase().includes(query)) {
          return true;
        }
      }
      return false;
    });
  }, [
    tasks,
    workGroupFilterId,
    statusFilter,
    assignmentFilter,
    deferredSearchQuery,
    customerNameById
  ]);

  const filteredWorkGroup = useMemo(
    () => workGroups.find((g) => String(g.id) === String(workGroupFilterId)) || null,
    [workGroups, workGroupFilterId]
  );

  useEffect(() => {
    if (workGroupFilterId && !filteredWorkGroup) {
      setWorkGroupFilterId('');
    }
  }, [workGroupFilterId, filteredWorkGroup]);

  const sortedTodayTasks = useMemo(() => {
    const day = new Date(`${todayKey}T12:00:00`);
    day.setHours(0, 0, 0, 0);
    return sortTodayTasks(filterTodayTasks(tasksForLists, day));
  }, [tasksForLists, todayKey]);
  const sortedUpcomingTasks = useMemo(() => {
    const day = new Date(`${todayKey}T12:00:00`);
    day.setHours(0, 0, 0, 0);
    return filterUpcomingTasks(tasksForLists, day);
  }, [tasksForLists, todayKey]);
  const sortedCompletedTasks = useMemo(() => {
    const day = new Date(`${todayKey}T12:00:00`);
    day.setHours(0, 0, 0, 0);
    return tasksForLists.filter((task) => isCompletedToday(task, day));
  }, [tasksForLists, todayKey]);
  const allCompletedTasks = useMemo(
    () => tasksForLists.filter((task) => task.status === 'completed' && !isTaskRemoved(task)),
    [tasksForLists]
  );
  const removedTasks = useMemo(
    () => (includeRemoved ? filterRemovedTasks(tasksForLists) : []),
    [tasksForLists, includeRemoved]
  );

  const heroImageUrl = useMemo(() => {
    const withBanner = sortedTodayTasks.find(
      (task) => task.effective_banner_image_url || task.banner_image_url || task.location_banner_image_url
    );
    return withBanner?.effective_banner_image_url
      || withBanner?.banner_image_url
      || withBanner?.location_banner_image_url
      || null;
  }, [sortedTodayTasks]);

  const focusedSectionRef = useRef(null);
  useEffect(() => {
    if (!focusSection) {
      focusedSectionRef.current = null;
    }
  }, [focusSection]);

  useEffect(() => {
    if (loading || !focusSection) {
      return undefined;
    }
    const sectionId = TASK_SECTION_IDS[focusSection];
    if (!sectionId || focusedSectionRef.current === focusSection) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (!el) {
        return;
      }
      focusedSectionRef.current = focusSection;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (typeof el.focus === 'function') {
        el.focus({ preventScroll: true });
      }
    }, 50);
    return () => window.clearTimeout(timer);
  }, [loading, focusSection]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="tasks">
      <div className="container container--wide main-content">
        <div className="page-header">
          <h1>{t('tasks:pageTitle')}</h1>
          <div className="tasks-header-actions">
            {canSubmitTaskDraft(user) && (
              <button
                type="button"
                className={`btn tasks-header-draft-btn ${isCompanyEmployee(user) ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => openDraftModal()}
                aria-label={t('tasks:draftsChipAria')}
                title={t('tasks:draftsChipAria')}
              >
                <span className="material-symbols-outlined tasks-header-draft-btn-icon" aria-hidden="true">
                  draft_orders
                </span>
                <span className="tasks-header-draft-btn-label">{t('tasks:draftsChip')}</span>
              </button>
            )}
            {isCompanyAdmin(user) && (
              <button
                type="button"
                className="btn btn-primary tasks-header-create-btn"
                onClick={() => (
                  upcomingViewMode === 'calendar'
                    ? openCreateFromCalendarSelection()
                    : openCreateModal()
                )}
              >
                <span className="material-symbols-outlined" aria-hidden="true">add</span>
                {t('tasks:create')}
              </button>
            )}
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        <TasksOverviewBanner
          imageUrl={heroImageUrl}
          activeCount={sortedTodayTasks.length}
          upcomingCount={sortedUpcomingTasks.length}
          calendarMode={upcomingViewMode === 'calendar'}
        />

        <TasksFiltersBar
          canUseWorkGroups={canUseWorkGroups}
          workGroups={workGroups}
          workGroupFilterId={workGroupFilterId}
          onWorkGroupFilterChange={setWorkGroupFilterId}
          filteredWorkGroup={filteredWorkGroup}
          onAssignAll={() => setShowAssignAllModal(true)}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          assignmentFilter={assignmentFilter}
          onAssignmentFilterChange={setAssignmentFilter}
          searchQuery={taskSearchQuery}
          onSearchQueryChange={setTaskSearchQuery}
          canToggleRemoved={isCompanyAdmin(user)}
          includeRemoved={includeRemoved}
          onToggleRemoved={() => setIncludeRemoved((prev) => !prev)}
        />

        <TaskListSection
          user={user}
          drafts={drafts}
          sortedTodayTasks={sortedTodayTasks}
          sortedUpcomingTasks={sortedUpcomingTasks}
          sortedCompletedTasks={sortedCompletedTasks}
          allCompletedTasks={allCompletedTasks}
          removedTasks={removedTasks}
          includeRemoved={includeRemoved}
          upcomingViewMode={upcomingViewMode}
          setUpcomingViewMode={setUpcomingViewMode}
          calendarMonth={calendarMonth}
          setCalendarMonth={setCalendarMonth}
          selectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
          onRowClick={handleRowClick}
          onClaim={handleClaim}
          onOpenCreateModal={() => openCreateModal()}
          onCreateForDay={openCreateModal}
          onNavigateDrafts={handleNavigateDrafts}
        />

        <TaskCreateModal
          isOpen={showModal}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
          locations={locations}
          customers={customers}
          articles={articles}
          workGroups={workGroups}
          canUseWorkGroups={canUseWorkGroups}
          taskPresets={taskPresets}
          presetChecklists={presetChecklists}
          handleTaskPresetChange={handleTaskPresetChange}
          handlePresetChecklistChange={handlePresetChecklistChange}
          newChecklistItem={newChecklistItem}
          setNewChecklistItem={setNewChecklistItem}
          handleAddChecklistItem={handleAddChecklistItem}
          handleRemoveChecklistItem={handleRemoveChecklistItem}
          user={user}
          assignableUsers={assignableUsers}
          selectedAssignees={selectedAssignees}
          setSelectedAssignees={setSelectedAssignees}
          setBannerFile={setBannerFile}
          bannerInputRef={bannerInputRef}
          onLocationCreated={fetchLocations}
          onCustomerCreated={fetchCustomers}
        />

        {canUseWorkGroups && (
          <WorkGroupAssignAllModal
            isOpen={showAssignAllModal}
            onClose={() => setShowAssignAllModal(false)}
            workGroup={filteredWorkGroup}
            assignableUsers={assignableUsers}
            onAssigned={async () => {
              await fetchTasks();
            }}
          />
        )}

        <TaskDraftModal
          isOpen={showDraftModal}
          onClose={handleCloseDraftModal}
          editingDraftId={editingDraftId}
          draftFormData={draftFormData}
          setDraftFormData={setDraftFormData}
          setDraftBannerFile={setDraftBannerFile}
          draftBannerInputRef={draftBannerInputRef}
          onSubmit={handleDraftSubmit}
          user={user}
        />

        {isCompanyAdmin(user) && (
          <button
            type="button"
            className="btn btn-primary tasks-create-fab"
            aria-label={t('tasks:fabCreateTask')}
            onClick={() => (
              upcomingViewMode === 'calendar'
                ? openCreateFromCalendarSelection()
                : openCreateModal()
            )}
          >
            <span className="tasks-create-fab-label">{t('tasks:fabCreateTask')}</span>
            <span className="tasks-create-fab-icon material-symbols-outlined" aria-hidden="true">add</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default Tasks;
